import React from 'react';
import { useTranslation } from 'react-i18next';

import { TSelectValue } from '@private/components';

import { PERMISSIONS } from 'api/auth/constants';
import AutocompleteSelect from 'components/Controls/AutocompleteSelect';
import SwitchInput from 'components/Controls/SwitchInput';
import { useFilterContext } from 'components/Grids/DataGrid/Filters/FilterContext';
import {
  BASE_MERCHANT_SCOPE_ITEM,
  ROLE_SCOPE,
} from 'components/Grids/DataGrid/Filters/utils/constants';
import {
  FILTER_OPERATIONS,
  IAppliedFilterValue,
  IScopeValue,
} from 'components/Grids/DataGrid/Filters/utils/types';
import Typography from 'components/Typography';
import { ICONS_MAP } from 'constants/icons';
import { appReactMemo } from 'hocs';
import { useRBAC } from 'hooks/useRBAC';
import { useGetMerchantsScope } from 'queries/merchants';
import { INullable } from 'utils/types';

import { BaseRemoveButton } from '../../../styled';

import { Container, RemoveSection, Row, Title } from './styled';

interface IRoleScope {
  appliedFilter: IAppliedFilterValue;
  fallbackOperation: FILTER_OPERATIONS;
  isAutoFocused?: boolean;
}

const RoleScope = ({ appliedFilter, fallbackOperation, isAutoFocused }: IRoleScope) => {
  const { t } = useTranslation();

  const { enabled: canViewScope } = useRBAC({
    list: [PERMISSIONS.CAN_VIEW_DETAILED_ROLE_SCOPE],
  });

  const ref = React.useRef<HTMLDivElement>(null);

  const { operation = fallbackOperation, value = {} } = appliedFilter || {};

  const { fullAccess = true, merchants = [BASE_MERCHANT_SCOPE_ITEM] } =
    value as IScopeValue;

  const { onSelectFilter, removeFilter } = useFilterContext();

  const { data: merchantsScope, isLoading: isLoadingScope } = useGetMerchantsScope(
    false,
    canViewScope,
  );
  const { merchantsMap = {}, organizations = [] } = merchantsScope || {};

  React.useEffect(() => {
    if (isAutoFocused) {
      setTimeout(() => ref.current?.scrollIntoView({ block: 'end' }), 300);
    }
  }, [isAutoFocused]);

  const onGlobalAccessChange = React.useCallback(
    (_e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      onSelectFilter({
        columnKey: ROLE_SCOPE,
        operation,
        value: {
          fullAccess: checked,
          merchants: checked ? [] : [BASE_MERCHANT_SCOPE_ITEM],
        },
      });
    },
    [onSelectFilter, operation],
  );

  const onMerchantChange = React.useCallback(
    (merchantIndex: INullable<number>) => (merchantId: TSelectValue) => {
      onSelectFilter({
        columnKey: ROLE_SCOPE,
        operation,
        value: {
          fullAccess,
          merchants: merchants.map((merchant, index) =>
            index === merchantIndex
              ? { fullAccess: false, id: merchantId as number, merchantTerminals: [] }
              : merchant,
          ),
        },
      });
    },
    [value],
  );

  const onMerchantAccessChange =
    (merchantIndex: number) =>
    (_e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      onSelectFilter({
        columnKey: ROLE_SCOPE,
        operation,
        value: {
          fullAccess,
          merchants: merchants.map((merchant, index) =>
            index === merchantIndex
              ? { fullAccess: checked, id: merchant.id, merchantTerminals: [] }
              : merchant,
          ),
        },
      });
    };

  const onMerchantTerminalsChange =
    (merchantIndex: number) => (selectedValues: TSelectValue) => {
      onSelectFilter({
        columnKey: ROLE_SCOPE,
        operation,
        value: {
          fullAccess,
          merchants: merchants.map((merchant, index) =>
            index === merchantIndex
              ? { ...merchant, merchantTerminals: selectedValues as number[] }
              : merchant,
          ),
        },
      });
    };

  const removeCurrentFilter = React.useCallback(() => {
    removeFilter({ columnKey: ROLE_SCOPE });
  }, []);

  const enableFilter = React.useCallback(() => {
    onSelectFilter({
      columnKey: ROLE_SCOPE,
      operation,
      value: { fullAccess: true, merchants: [] },
    });
  }, []);

  const addMerchant = React.useCallback(() => {
    onSelectFilter({
      columnKey: ROLE_SCOPE,
      operation,
      value: {
        fullAccess,
        merchants: [...merchants, BASE_MERCHANT_SCOPE_ITEM],
      },
    });
  }, [value]);

  const removeOrganization = (merchantIndex: number) => () => {
    onSelectFilter({
      columnKey: ROLE_SCOPE,
      operation,
      value: {
        fullAccess,
        merchants:
          merchants.length === 1
            ? [BASE_MERCHANT_SCOPE_ITEM]
            : merchants.filter((_, index) => index !== merchantIndex),
      },
    });
  };

  const onActivateFilter = React.useCallback(
    (_e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      if (checked) {
        enableFilter();
      } else {
        removeCurrentFilter();
      }
    },
    [],
  );

  return (
    <Container ref={ref}>
      <SwitchInput
        checked={!!appliedFilter?.value}
        label={t('common.filters.activateFilter')}
        onChange={onActivateFilter}
      />
      {!!appliedFilter?.value && (
        <SwitchInput
          checked={fullAccess}
          label={t('common.filters.fullAccess')}
          onChange={onGlobalAccessChange}
        />
      )}
      {!fullAccess &&
        merchants.map(
          (
            { fullAccess: merchantFullAccess, id: merchantId, merchantTerminals },
            index,
          ) => (
            <Row key={`${merchantId}-${index}`}>
              <Title>
                <Typography size='xs' variant='regular'>
                  {`${t('common.filters.access')} ${index + 1}`}
                </Typography>
                {!(merchants.length === 1 && !merchantId) && (
                  <RemoveSection
                    iconSize={6}
                    onClick={removeOrganization(index)}
                    size='xxs'
                    startIcon={<ICONS_MAP.Close />}
                    variant='danger'
                  />
                )}
              </Title>
              <AutocompleteSelect
                loading={isLoadingScope}
                onChange={onMerchantChange(index)}
                options={organizations}
                placeholder={t('organizations.organization')}
                size='sm'
                value={merchantId}
              />
              <SwitchInput
                checked={merchantFullAccess}
                disabled={!merchantId}
                label={t('common.filters.fullAccess')}
                onChange={onMerchantAccessChange(index)}
              />
              <AutocompleteSelect
                disableCloseOnSelect
                disabled={!merchantId || merchantFullAccess}
                disableUnselect={false}
                inputReadOnly={false}
                loading={isLoadingScope}
                maxVisibleMultiselectItems={1}
                multiselect
                onChange={onMerchantTerminalsChange(index)}
                options={(merchantId && merchantsMap[merchantId]) || []}
                placeholder={t('merchant.title')}
                size='sm'
                value={merchantTerminals}
              />
            </Row>
          ),
        )}
      {!fullAccess && (
        <BaseRemoveButton
          disabled={!merchants[merchants.length - 1].id}
          iconSize={14}
          onClick={addMerchant}
          size='sm'
          startIcon={<ICONS_MAP.Plus />}
          variant='transparentPrimary'
        >
          {t('common.filters.addValue')}
        </BaseRemoveButton>
      )}
    </Container>
  );
};

export default appReactMemo(RoleScope);
